import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useQuestion} from '../../../../hooks/question';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {AreaModel} from '../../../../store/control/state';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';

export const RoomEditScreen = memo(function RoomEditScreen() {
    const params = useParams<{ id: string; floor_id: string; }>();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [rpc] = useRPC();
    const history = useHistory();

    const floorList = useFilterSelector(state => state.control.areaList, area => area.type === 'floor', []);
    const roomList = useFilterSelector(state => state.control.areaList, area => area.type === 'room', []);

    const [selected, setSelected] = useState<AreaModel>(null);
    const [backURL, setBackURL] = useState('/settings/project-information');
    const [name, setName] = useState('');
    const [floor, setFloor] = useState<SelectItem<AreaModel>>(null);

    const floorOptionList: SelectItem<AreaModel>[] =
        useMemo(() => floorList.map(floor => ({value: floor, label: floor.name})), [floorList]);

    const onBackPressed = useCallback(() => {
        history.push(backURL);
    }, [history, backURL]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen geçerli bir ad giriniz.');

            return false;
        }

        if (floor == null || floor.value == null) {
            failure('Lütfen kat seçiniz.');

            return false;
        }

        return true;
    }, [failure, floor, name]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const areaModel: AreaModel = {
            id: selected != null ? selected.id : null,
            name,
            type: 'room',
            parent_area_id: floor.value.id,
        };

        return rpc.updateArea(areaModel)
            .then(() => {
                success('Oda başarılı bir şekilde ' + (areaModel.id == null ? 'oluşturuldu.' : 'düzenlendi.'));
                onBackPressed();
            }).catch(failure);
    }, [
        selected,
        isFieldsValid,
        name,
        floor,
        success,
        failure,
        onBackPressed,
        rpc,
    ]);

    const onDeletePressed = useCallback(() => {
        question('Odayı silmek istediğinizden emin misiniz?', () => {
            return rpc.deleteArea(selected.id)
                .then(() => {
                    success(`Oda başarılı bir şekilde silindi.`);
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [failure, onBackPressed, question, rpc, selected, success]);

    const buttons = useMemo(() =>
        [
            {
                text: 'SİL',
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: 'KAYDET',
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: 'İPTAL',
                className: 'red',
                url: backURL,
            },
        ], [onDeletePressed, selected, onSavePressed, backURL],
    );

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    useEffect(() => {
        if (params.floor_id != null) {
            const floor = floorList.find(floor => floor.id === params.floor_id);
            if (floor) {
                setBackURL(`/settings/floor/${floor.id}/room`);
            }
            setFloor(floorOptionList.find(floorOption => floorOption.value.id === floor.id));
        }

        if (params.id == null) {
            return;
        }

        const room = roomList.find(room => room.id === params['id']);
        if (room == null) {
            return;
        }

        setSelected(room);
        setName(room.name);
    }, [floorList, floorOptionList, params, roomList]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={backURL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Oda adı yazınız...'
                            label='Oda Adı'
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label='Kat'
                            options={floorOptionList}
                            onChange={setFloor}
                            value={floor}/>
                    </div>
                    <div className='content-wrapper'>

                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
